import { Children, Fragment, ReactNode, isValidElement, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';
import {
  DASHED_RULE_STYLE,
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CLOSE_X,
  MODAL_FOOT,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from './designTokens';

/**
 * Shell del modal — Claude Design (NuevoProductoModal.dc.html /
 * EditarSucursalModal.dc.html).
 *
 * El diseño arma el modal como una hoja de tres bandas:
 *
 *   HEAD  baldosa de icono en degradado + kicker mono + titulo grande +
 *         descripcion, con un slot a la derecha para el estado (pill) y el
 *         boton de cierre; cierra con la regla dasheada del sistema.
 *   BODY  scroll propio, padding 36/28px.
 *   FOOT  fondo gris apenas mas frio, ayuda a la izquierda y acciones a la
 *         derecha.
 *
 * Radio 20px y sombra larga (0 40px 100px -20px) lo despegan del velo navy.
 *
 * La banda de cuerpo se aplica sola: si el caller no pone <ModalBody>, sus
 * hijos se envuelven en la banda con el padding y el scroll del diseño. Antes
 * ese contenido quedaba pegado a los bordes del panel — de ahi la sensacion de
 * modal "simple".
 *
 * API compatible con el uso previo: `title`, `kicker`, `description`, `icon` y
 * `footer` siguen igual; se agregan `badge` (pill de estado) y `stepper`.
 */

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Titulo grande (h3). Si se omite y no hay `icon`/`kicker`, no se renderiza la cabecera. */
  title?: string;
  /** Kicker mono/uppercase encima del titulo (contexto de dominio). */
  kicker?: string;
  /** Descripcion breve debajo del titulo. */
  description?: string;
  /** Icono a la izquierda del titulo (usa Heroicons stroke). */
  icon?: ReactNode;
  /** Pill de estado a la derecha de la cabecera, antes del boton de cierre. */
  badge?: ReactNode;
  /** Fila extra bajo el titulo (por ejemplo <ModalStepper />). */
  stepper?: ReactNode;
  /** Slot de pie con divider superior; se ancla al bottom del panel. */
  footer?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | 'full';
  showCloseButton?: boolean;
  hideBackdrop?: boolean;
  /** Clases extra para la banda de cuerpo automatica. */
  bodyClassName?: string;
  /** Renderiza los hijos sin la banda de cuerpo (contenido a sangre). */
  unpadded?: boolean;
  'data-testid'?: string;
  /**
   * `data-testid` del boton de cerrar (la X). El diseño movio ese boton del
   * markup de cada modal al Modal compartido, pero la automatizacion de QA lo
   * sigue buscando con el nombre propio de cada pantalla.
   */
  closeTestId?: string;
  /**
   * Capa de apilamiento (z-index). Por defecto 50, donde vive cualquier modal
   * del portal; el modal de error del asistente usa una mayor.
   *
   * No es sólo cosmético: la capa es lo que decide qué modal manda cuando hay
   * varios abiertos. Ver `modalesAbiertos` más abajo.
   */
  layer?: number;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-5xl',
  '4xl': 'max-w-6xl',
  full: 'max-w-7xl',
};

/**
 * Modales abiertos ahora mismo, con su capa y su forma de cerrarse. Clave: un
 * objeto único por instancia (dos modales pueden compartir capa, así que la
 * capa no sirve como clave). Se lee en el momento del clic, así que siempre
 * refleja el estado real.
 */
const modalesAbiertos = new Map<object, { capa: number; cerrar: () => void }>();

/** El modal abierto de capa más alta, o null si no hay ninguno. */
function modalMasAlto() {
  let masAlto: { capa: number; cerrar: () => void } | null = null;
  for (const abierto of modalesAbiertos.values()) {
    if (!masAlto || abierto.capa > masAlto.capa) masAlto = abierto;
  }
  return masAlto;
}

export function Modal({
  isOpen,
  onClose,
  title,
  kicker,
  description,
  icon,
  badge,
  stepper,
  footer,
  children,
  size = 'md',
  showCloseButton = true,
  hideBackdrop = false,
  bodyClassName,
  unpadded = false,
  'data-testid': testId,
  closeTestId,
  layer = 50,
}: ModalProps) {
  const hasHeader = Boolean(title || kicker || icon);

  /*
   * Si el caller ya trae su propia banda (<ModalBody> / <ModalFooter>), se
   * respeta tal cual; si no, se envuelve para que el contenido reciba el
   * padding y el scroll del diseño en vez de quedar pegado al borde.
   */
  const bringsOwnBands = Children.toArray(children).some(
    (child) => isValidElement(child) && (child.type === ModalBody || child.type === ModalFooter)
  );
  const wrapBody = !unpadded && !bringsOwnBands;

  // Un modal NO debe cerrarse por un clic que ocurrió dentro de otro modal
  // abierto por encima suyo.
  //
  // `Dialog` de HeadlessUI llama a `onClose` tanto con Escape como con un clic
  // "fuera del panel", y cualquier clic dentro de OTRO diálogo cuenta como
  // fuera. El modal de error del asistente cuelga del SupportErrorProvider y el
  // de formulario de la pantalla: son ramas distintas del árbol de React, así
  // que HeadlessUI no los reconoce como anidados y no los apila. Resultado:
  // cerrar el de error cerraba también el formulario de abajo, y el usuario
  // perdía lo que había escrito.
  //
  // Subir el z-index NO alcanza (probado): la detección de "clic fuera" es por
  // DOM, no por capa. Lo que sí resuelve es que cada modal sepa si hay otro
  // ENCIMA y, en ese caso, le PASE el cierre en lugar de cerrarse él.
  //
  // La delegación no es un adorno: HeadlessUI entrega el Escape a UN solo
  // diálogo, y no necesariamente al de arriba. Medido con dos modales abiertos,
  // el evento llega al del formulario (capa 50) y nunca al de error (capa 60).
  // Si el de abajo se limitara a ignorarlo, la tecla no cerraría nada: ni el de
  // abajo (bien) ni el de arriba (que es el que el usuario quiere cerrar).
  //
  // El registro vive a nivel de módulo y no en un contexto justamente porque
  // los modales que se apilan no comparten ancestro.
  const identidad = useRef({});

  // El cierre se guarda por ref para que el registro no capture un `onClose`
  // viejo: el efecto sólo vuelve a correr si cambian `isOpen` o `layer`.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const id = identidad.current;
    modalesAbiertos.set(id, { capa: layer, cerrar: () => onCloseRef.current() });
    return () => {
      modalesAbiertos.delete(id);
    };
  }, [isOpen, layer]);

  const manejarCierre = () => {
    const masAlto = modalMasAlto();
    // Se delega al `onClose` del de arriba, nunca a su `manejarCierre`: así la
    // cadena termina en un cierre real y no puede rebotar entre instancias.
    if (masAlto && masAlto.capa > layer) {
      masAlto.cerrar();
      return;
    }
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative" style={{ zIndex: layer }} onClose={manejarCierre}>
        {/*
          El condicional va AFUERA de Transition.Child (no adentro, sobre su
          hijo): con `hideBackdrop`, el hijo quedaba en `false` y
          Transition.Child (as={Fragment}) no tenia un elemento real donde
          reenviar el `ref` de la transicion -- Headless UI lo resolvia
          renderizando un Fragment propio y tiraba "Passing props on
          Fragment!". Repro: EditCompanyPage > "Nueva Empresa"
          (NewCompanyWizardModal usa hideBackdrop).
        */}
        {!hideBackdrop && (
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={MODAL_BACKDROP} />
          </Transition.Child>
        )}

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid={testId}
                className={cn(
                  // max-h + flex-col: el panel nunca excede la ventana; su
                  // contenido usa flex-1 para llenar el espacio disponible y
                  // hacer scroll interno si sobra alto, en vez de crecer fuera
                  // del viewport.
                  'flex max-h-[calc(100vh-2rem)] flex-col',
                  MODAL_PANEL,
                  sizeClasses[size]
                )}
              >
                {hasHeader && (
                  <div className={cn(MODAL_HEAD, 'shrink-0')}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3.5">
                        {icon && <span className={MODAL_ICON_TILE}>{icon}</span>}
                        <div className="min-w-0">
                          {kicker && <div className={MODAL_KICKER}>{kicker}</div>}
                          {title && (
                            <Dialog.Title as="h3" className={cn(MODAL_TITLE, kicker && 'mt-1')}>
                              {title}
                            </Dialog.Title>
                          )}
                          {description && (
                            <p className="mt-1.5 text-[14px] leading-[1.5] text-secondary-500 dark:text-secondary-400">
                              {description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-3.5">
                        {badge}
                        {showCloseButton && (
                          <button
                            type="button"
                            onClick={onClose}
                            aria-label="Cerrar"
                            data-testid={closeTestId}
                            className={MODAL_CLOSE_X}
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {stepper && <div className="mt-[22px]">{stepper}</div>}
                  </div>
                )}

                {/*
                  Fallback: si no hay cabecera (title/kicker/icon), el boton X
                  igual se dibuja en la esquina superior derecha, superpuesto al
                  contenido, para no perder la salida.
                */}
                {!hasHeader && showCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Cerrar"
                    data-testid={closeTestId}
                    className={cn(MODAL_CLOSE_X, 'absolute right-5 top-5 z-10')}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}

                {wrapBody ? (
                  <div className={cn(MODAL_BODY, 'min-h-0 flex-1', bodyClassName)}>{children}</div>
                ) : (
                  children
                )}

                {footer && <div className={cn(MODAL_FOOT, 'shrink-0')}>{footer}</div>}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export function ModalHeader({ children }: { children: ReactNode }) {
  return <div className="mb-4">{children}</div>;
}

/**
 * Cuerpo del modal. Padding y scroll del diseño (36/28px). El alto default
 * `h-[70vh]` de la version anterior se reemplaza por `flex-1 min-h-0`: el panel
 * ya limita el alto total, asi que el cuerpo toma el espacio que sobra y hace
 * scroll solo cuando hace falta, en vez de reservar 70vh siempre.
 */
export function ModalBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn(MODAL_BODY, 'flex min-h-0 flex-1 flex-col', className)}>{children}</div>
  );
}

/**
 * Pie con divider superior + fondo apenas mas frio que el blanco. `justify-end`
 * por default; con <ModalFooterHint> como primer hijo el layout pasa a
 * "ayuda a la izquierda / acciones a la derecha" como en el diseño.
 */
export function ModalFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn(MODAL_FOOT, 'shrink-0 justify-end', className)}>{children}</div>
  );
}

/**
 * Ayuda del pie ("Los campos * son obligatorios"), con el punto ambar del
 * diseño. Va como primer hijo de <ModalFooter> junto a un contenedor de
 * acciones, y empuja las acciones a la derecha.
 */
export function ModalFooterHint({
  children = (
    <>
      Los campos <span className="font-bold text-[#b8503a]">*</span> son obligatorios
    </>
  ),
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mr-auto flex items-center gap-2 text-[13px] text-secondary-500 dark:text-secondary-400',
        className
      )}
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#fada51] shadow-[0_0_6px_rgba(250,218,81,.6)]"
      />
      <span>{children}</span>
    </div>
  );
}

/**
 * Encabezado numerado de seccion — el patron "01 · Identificación" del sistema
 * visual, para segmentar formularios largos dentro del modal.
 */
export function ModalSection({
  index,
  title,
  className,
}: {
  index: string;
  title: string;
  className?: string;
}) {
  return (
    <div className={cn('mb-4 flex items-center gap-3.5', className)}>
      <span className="font-mono text-[12px] font-bold tabular-nums tracking-[0.12em] text-secondary-400">
        {index}
      </span>
      <span className="text-[14px] font-bold tracking-[-0.015em] text-ink-850 dark:text-white">
        {title}
      </span>
      <span className="h-px flex-1" style={DASHED_RULE_STYLE} aria-hidden="true" />
    </div>
  );
}

/**
 * Bloque de seccion del modal (`.sec` del diseño): el numero en teal vive en
 * una columna propia de 44px y el contenido en la otra, de modo que los campos
 * quedan alineados entre secciones. Secciones consecutivas se separan con la
 * regla dasheada.
 *
 * `divider={false}` en la primera para que no arranque con una linea suelta.
 */
export function ModalSectionBlock({
  index,
  title,
  aside,
  divider = true,
  children,
  className,
}: {
  index: string;
  title: string;
  /** Pill opcional al lado del titulo (por ejemplo "OPCIONAL"). */
  aside?: ReactNode;
  divider?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-[28px_minmax(0,1fr)] gap-4 sm:grid-cols-[44px_minmax(0,1fr)] sm:gap-5',
        divider
          ? 'mt-[22px] border-t border-dashed border-[#eef0f2] pt-[22px] dark:border-secondary-700'
          : 'pt-1',
        className
      )}
    >
      <div className="pt-0.5 font-mono text-[14px] font-bold tabular-nums tracking-[0.06em] text-teal-500">
        {index}
      </div>
      <div className="min-w-0">
        <h3 className="mb-4 flex flex-wrap items-center gap-2.5 text-base font-extrabold tracking-[-0.015em] text-ink-850 dark:text-white">
          {title}
          {aside}
        </h3>
        {children}
      </div>
    </div>
  );
}

/**
 * Pill de la cabecera / de un titulo de seccion ("BORRADOR", "OPCIONAL").
 */
export function ModalPill({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'draft' | 'active';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-[#f4f4f4] text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300',
    draft: 'bg-[#fada51]/20 text-[#7a5a06] dark:text-[#fada51]',
    active: 'bg-teal-500/10 text-[#0d9488] dark:text-teal-300',
  } as const;
  const dots = { neutral: '#a1a1aa', draft: '#d4a814', active: '#0d9488' } as const;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.10em]',
        tones[tone],
        className
      )}
    >
      <span
        aria-hidden="true"
        className="h-[5px] w-[5px] rounded-full"
        style={{ background: dots[tone] }}
      />
      {children}
    </span>
  );
}

/**
 * Stepper del diseño (EditarSucursalModal.dc.html): el paso cumplido lleva el
 * numero en degradado con halo, el pendiente queda en blanco con borde, y el
 * conector se rellena cuando el tramo ya se recorrio.
 *
 * Solo presentacion: recibe los rotulos y el indice actual, no decide nada.
 */
export function ModalStepper({
  steps,
  current,
  className,
  testIdBase,
}: {
  steps: string[];
  /** Indice (base 0) del paso activo. */
  current: number;
  className?: string;
  /**
   * Prefijo para el `data-testid` de cada paso: se emite
   * `${testIdBase}-${index}`. Lo usa la automatizacion de QA, que localiza los
   * pasos del wizard uno por uno.
   */
  testIdBase?: string;
}) {
  return (
    <div className={cn('flex items-center', className)}>
      {steps.map((label, index) => {
        const done = index <= current;
        return (
          <Fragment key={label}>
            {index > 0 && (
              <div className="relative mx-2 h-0.5 flex-1 overflow-hidden rounded-full bg-[#eef2f6] sm:mx-4 dark:bg-secondary-700">
                {done && (
                  <span className="absolute inset-0 bg-gradient-to-r from-teal-500 to-[#6b239e] shadow-[0_0_8px_rgba(18,196,178,.4)]" />
                )}
              </div>
            )}
            <div
              className="flex shrink-0 items-center gap-3"
              data-testid={testIdBase ? `${testIdBase}-${index}` : undefined}
            >
              <span
                className={cn(
                  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-extrabold tabular-nums',
                  done
                    ? 'bg-gradient-to-br from-teal-500 to-[#6b239e] text-white shadow-[0_0_0_4px_#ffffff,0_0_0_6px_rgba(18,196,178,.22)] dark:shadow-[0_0_0_4px_theme(colors.secondary.800),0_0_0_6px_rgba(18,196,178,.22)]'
                    : 'border-[1.5px] border-[#e2e8f0] bg-white text-secondary-400 dark:border-secondary-600 dark:bg-secondary-800'
                )}
              >
                {index + 1}
              </span>
              <span
                className={cn(
                  'hidden text-sm sm:block',
                  done
                    ? 'font-bold text-ink-850 dark:text-white'
                    : 'font-semibold text-secondary-400'
                )}
              >
                {label}
              </span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
